import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { setupDatabase } from './utils/setupDatabase.ts';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StorageService } from '@/services/storage/StorageService';
import ScrollToTop from './components/ScrollToTop';

// Create a client
const queryClient = new QueryClient();

// Handle uncaught runtime errors gracefully
window.addEventListener('unhandledrejection', (event) => {
  // Prevent console errors for message port closed
  if (event.reason?.message?.includes('message port closed')) {
    event.preventDefault();
    return;
  }
  console.error('Unhandled promise rejection:', event.reason);
});

// Make sure we have a root element to mount the app
const rootElement = document.getElementById("root");
if (!rootElement) {
  console.error("Failed to find the root element");
  const body = document.body;
  const newRoot = document.createElement("div");
  newRoot.id = "root";
  body.appendChild(newRoot);
}

// Initialize the app
async function initializeApp() {
  try {
    // Setup database first
    await setupDatabase();
    
    // Mount the app - IMPORTANT: BrowserRouter must be outside AuthProvider
    createRoot(document.getElementById("root")!).render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ 
            v7_startTransition: true,
            v7_relativeSplatPath: true 
          }}>
            <ScrollToTop />
            <ThemeProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </ThemeProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Error initializing app:', error);
  }
}

initializeApp();
